import {
    IntegrationConnectionStatus,
    IntegrationProvider,
    INTEGRATION_PROVIDER_LABELS,
    type IntegrationConnectionViewDto
} from '@/types/integrations'

/**
 * Mirrors CrmProviders.cs. A company connects at most one of these at a time: a deal that exists in
 * two CRMs produces two attribution rows with no shared identity to collapse them on, so it would be
 * counted twice in pipeline and closed-won.
 *
 * The server refuses the second connection regardless of what this file says. This exists so the UI
 * can explain the rule up front instead of letting the operator find it in an error.
 */
const CRM_PROVIDERS: string[] = [
    IntegrationProvider.Salesforce,
    IntegrationProvider.HubSpot,
    IntegrationProvider.Dynamics
]

const sameProvider = (left: string, right: string) => left.toLowerCase() === right.toLowerCase()

// Matched case-insensitively for the same reason the provider ids are elsewhere on this screen:
// a casing mismatch from the backend would otherwise silently fall back to the raw enum name and
// show "Dynamics" where the product is called "Dynamics 365".
const label = (provider: string) => {
    const key = Object.keys(INTEGRATION_PROVIDER_LABELS).find(candidate => sameProvider(candidate, provider))
    return key ? INTEGRATION_PROVIDER_LABELS[key] : provider
}

export const isCrmProvider = (provider?: string | null): boolean =>
    !!provider && CRM_PROVIDERS.some(crm => sameProvider(crm, provider))

/**
 * Broken until an admin acts: an expired login to reconnect, or a sync stopped after repeated
 * failures. Mirrors CrmProviders.NeedsAdminAttention, which owns the rule and decides who is emailed
 * (ORB-966). Not CRM-specific — BigMarker's health reads the same statuses.
 */
export const needsAdminAttention = (status?: string | null): boolean =>
    status === IntegrationConnectionStatus.Error || status === IntegrationConnectionStatus.AuthExpired

/** The company's CRM connection when it needs an admin, or null. Archived means disconnected on purpose. */
export const findBrokenCrm = (connections: IntegrationConnectionViewDto[] | null): IntegrationConnectionViewDto | null =>
    connections?.find(connection =>
        isCrmProvider(connection.Provider as string)
        && !connection.IsArchived
        && needsAdminAttention(connection.Status as string)
    ) ?? null

export const crmLabel = label

/**
 * The CRM holding the company's slot, or null. "Connected" is the server's predicate — Active and
 * not archived — because a disconnected CRM keeps its record, and its mere presence must not read as
 * connected or the company could never switch.
 */
export const findConnectedCrm = (connections: IntegrationConnectionViewDto[] | null): string | null =>
    connections?.find(connection =>
        isCrmProvider(connection.Provider as string)
        && !connection.IsArchived
        && connection.Status === IntegrationConnectionStatus.Active
    )?.Provider as string ?? null

/** Short enough for a provider card. Null when this provider is free to connect. */
export const crmBlockedReason = (connectedCrm: string | null, provider: string): string | null =>
    connectedCrm && isCrmProvider(provider) && !sameProvider(connectedCrm, provider)
        ? `Disconnect ${label(connectedCrm)} first. One CRM at a time, so a deal in both isn't counted twice.`
        : null

export const crmBlockedHeadline = (connectedCrm: string): string =>
    `${label(connectedCrm)} is your connected CRM`

/** The panel-sized version, where there is room to say why. */
export const crmBlockedDetail = (connectedCrm: string, provider: string): string =>
    `Only one CRM connects at a time, so a deal that exists in both can't be counted twice in `
    + `pipeline and closed-won. Disconnect ${label(connectedCrm)} to connect ${label(provider)} instead.`
